import { Button, Drawer, Loading, Select, Title } from "@components";

import { OnboardingAPI } from "providers/OnboardingAPI";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { ForEach, If } from "shared/components/control-statements";
import { Interrogation } from "shared/components/Icons";
import { InputSearch } from "shared/components/InputSearch";
import Toggle2 from "shared/components/Toggle2";
import { useDragScroll } from "shared/hooks/useDragScroll";
import { EnvironmentStorage } from "shared/tools/EnvironmentStorage";
import styles from "styles/onboarding.module.scss";

import classNames from "classnames/bind";
import "leaflet/dist/leaflet.css";
import { EnvironmentsAPI } from "providers/EnvironmentsAPI";

const css = classNames.bind(styles);

function isUUID(value) {
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(value);
}

export default function OnboardingMapping() {
  const environment = EnvironmentStorage.getEnvironment();

  const [search, setSearch] = useState("");
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openSuccess, setOpenSuccess] = useState(false);
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [openError, setOpenError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [title, setTitle] = useState("");

  const navigate = useNavigate();

  const { scrollRef, onMouseDown, wasDragging } = useDragScroll();

  function getCompletionPercentage() {
    if (!Array.isArray(mappings) || mappings.length === 0) return 0;

    const requiredFields = ["pdv", "logistic"];
    const totalFields = mappings.length * requiredFields.length;

    let filledCount = 0;

    for (const item of mappings) {
      for (const field of requiredFields) {
        const value = item[field];
        if (!value) continue;

        filledCount++;
      }
    }

    const percentage = (filledCount / totalFields) * 100;
    return Number(percentage.toFixed(0));
  }

  const filteredUnits = mappings.filter((mapping) => {
    return mapping.name.toLowerCase().includes(search.toLowerCase());
  });

  const finalUnits = filteredUnits;

  const handleMappingChange = (mappingId, fieldPath, value) => {
    setMappings((prevMappings) => {
      const newMappings = [...prevMappings];
      const index = newMappings.findIndex((b) => b.id === mappingId);
      if (index !== -1) {
        const pathParts = fieldPath.split(".");
        let target = newMappings[index];

        for (let i = 0; i < pathParts.length - 1; i++) {
          const key = pathParts[i];

          if (!target[key]) target[key] = {};
          target = target[key];
        }

        target[pathParts[pathParts.length - 1]] = value;
      }
      return newMappings;
    });
  };

  const getMappings = async () => {
    const response = (await OnboardingAPI.getMappings()) || [];
    setMappings(response || []);
    setLoading(false);
  };

  const save = async () => {
    setLoading(true);

    const mappingsWithoutId = mappings.map((b) => {
      if (isUUID(b.id)) delete b.id;
      return b;
    });

    const response = await OnboardingAPI.putMappings(mappingsWithoutId);

    if (response.getErrors({}).length > 0) {
      setOpenError(true);
      setErrorMessage(response.getErrors({})[0]);
    } else {
      setOpenSuccess(true);
      getMappings();
    }

    setLoading(false);
  };

  const getBrands = async () => {
    const response = await OnboardingAPI.getBrands();
    if (response) {
      setSelectedBrand(response[0]?.id);
      setBrands(response);
    }
  };

  useEffect(() => {
    getBrands();
    getMappings();
  }, []);

  const getHolding = async () => {
    const environmentId = await EnvironmentStorage.getEnvironmentHoldingId();

    const holdingResponse = await EnvironmentsAPI.getById(environmentId);
    const holding = holdingResponse.getData({});
    if (holding.name) setTitle(holding.name || environment?.brand?.name);
  };

  useEffect(() => {
    getHolding();
  }, []);

  return (
    <div className={styles.onboardingComponent}>
      <If cond={loading}>
        <Loading onboarding style={{ top: 95 }} />
      </If>
      <div className={styles.onboardingHeader}>
        <div className={styles.content}>
          <Title
            title="Mapeamento"
            backTo={{ name: title, url: "/implantacao" }}
          >
            <div className="d-flex align-center gap-10">
              <If cond={mappings.length}>
                <InputSearch
                  placeholder="Buscar por..."
                  onChange={(e) => setSearch(e.target.value)}
                  design="four"
                />
              </If>

              {/* <Interrogation /> */}
            </div>
            <div className={styles.divider} />
            <div className={styles.completeness}>
              <div
                className={styles.total}
                style={{ "--percent": getCompletionPercentage() }}
              >
                <span>{getCompletionPercentage()}%</span>
              </div>
            </div>
          </Title>
        </div>
      </div>

      <div className={styles.onboardingContainer}>
        <If cond={brands.length > 1}>
          <div className={styles.brands}>
            <ForEach
              items={brands}
              render={(item) => {
                return (
                  <div
                    className={css({
                      brand: true,
                      selected: selectedBrand === item.id,
                    })}
                    onClick={() => setSelectedBrand(item.id)}
                  >
                    <p className={styles.name}>{item.name}</p>
                  </div>
                );
              }}
            />
          </div>
        </If>

        <div
          onMouseDown={onMouseDown}
          ref={scrollRef}
          className={styles.table}
          style={{ maxHeight: "calc(100dvh - 220px)" }}
        >
          <table>
            <thead>
              <tr>
                <th
                  style={{
                    position: "sticky",
                    left: 0,
                    zIndex: 200,
                    paddingLeft: 45,
                  }}
                >
                  NOME DA UNIDADE
                </th>
                <th>PDV</th>
                <th>FORNECEDOR LOGÍSTICO</th>
                <th>SALÃO BALCÃO</th>
                <th>AUTOATENDIMENTO WINDOWS</th>
                <th>AUTOATENDIMENTO ANDROID</th>
                <th>TABLET NA MESA</th>
                <th>QR CODE NA MESA</th>
                <th>TABLE SERVICE (SMARTPOS)</th>
                <th>TABLET ADQUIRENTE (SMARTPOS)</th>
                <th>PAGAMENTO NA ENTREGA</th>
              </tr>
            </thead>
            <tbody>
              <ForEach
                items={finalUnits}
                render={(unit) => {
                  if (unit.brand_id !== selectedBrand) return null;

                  return (
                    <tr key={unit.id}>
                      <td
                        style={{
                          minWidth: 300,
                          position: "sticky",
                          left: 0,
                          zIndex: 200,
                          paddingLeft: 45,
                        }}
                      >
                        {unit.name}
                      </td>
                      <td style={{ minWidth: 300 }}>
                        <Select
                          design="mini"
                          value={unit.pdv}
                          options={[
                            { label: "---", value: "" },
                            { label: "3SCheckout", value: "3SCheckout" },
                            { label: "Altec", value: "Altec" },
                            { label: "Atena.ai", value: "Atena" },
                            { label: "BERP", value: "BERP" },
                            { label: "Colibri", value: "Colibri" },
                            { label: "DFranquias", value: "DFranquias" },
                            { label: "EasyAssist", value: "EasyAssist" },
                            { label: "Eclética", value: "Eclética" },
                            { label: "EPOC", value: "EPOC" },
                            { label: "Menew", value: "Menew" },
                            { label: "New Choice", value: "New" },
                            { label: "RS Solutions", value: "RS" },
                            { label: "Saipos", value: "Saipos" },
                            { label: "Sinc Fácil", value: "Sinc" },
                            { label: "Oracle Symphony", value: "Oracle" },
                            { label: "Teknisa", value: "Teknisa" },
                            { label: "Outro", value: "Outro" },
                          ]}
                          onChange={(e) =>
                            handleMappingChange(unit.id, "pdv", e.target.value)
                          }
                          filled={!!unit.pdv}
                        />
                      </td>
                      <td style={{ minWidth: 300 }}>
                        <Select
                          design="mini"
                          value={unit.logistic}
                          options={[
                            { label: "---", value: "" },
                            { label: "Bee Delivery", value: "Bee Delivery" },
                            { label: "iFood Entrega", value: "iFood Entrega" },
                            { label: "Mottu", value: "Mottu" },
                            {
                              label: "Foody Delivery",
                              value: "Foody Delivery",
                            },
                            {
                              label: "Soldado de Entrega",
                              value: "Soldado de Entrega",
                            },
                            { label: "Outro", value: "Outro" },
                          ]}
                          onChange={(e) =>
                            handleMappingChange(
                              unit.id,
                              "logistic",
                              e.target.value
                            )
                          }
                          filled={!!unit.logistic}
                        />
                      </td>
                      <td style={{ minWidth: 200 }}>
                        <div className="d-flex align-center justify-between">
                          <Toggle2
                            showText
                            checked={unit.delivery?.indoor}
                            onChange={() => {
                              handleMappingChange(
                                unit.id,
                                "delivery.indoor",
                                !unit.delivery?.indoor
                              );
                            }}
                          />
                        </div>
                      </td>
                      <td style={{ minWidth: 200 }}>
                        <div className="d-flex align-center justify-between">
                          <Toggle2
                            showText
                            checked={unit.totem?.windows}
                            onChange={() => {
                              handleMappingChange(
                                unit.id,
                                "totem.windows",
                                !unit.totem?.windows
                              );
                            }}
                          />
                        </div>
                      </td>
                      <td style={{ minWidth: 200 }}>
                        <div className="d-flex align-center justify-between">
                          <Toggle2
                            showText
                            checked={unit.totem?.android}
                            onChange={() => {
                              handleMappingChange(
                                unit.id,
                                "totem.android",
                                !unit.totem?.android
                              );
                            }}
                          />
                        </div>
                      </td>
                      <td style={{ minWidth: 200 }}>
                        <div className="d-flex align-center justify-between">
                          <Toggle2
                            showText
                            checked={unit.table?.tablet}
                            onChange={() => {
                              handleMappingChange(
                                unit.id,
                                "table.tablet",
                                !unit.table?.tablet
                              );
                            }}
                          />
                        </div>
                      </td>
                      <td style={{ minWidth: 200 }}>
                        <div className="d-flex align-center justify-between">
                          <Toggle2
                            showText
                            checked={unit.table?.qrcode}
                            onChange={() => {
                              handleMappingChange(
                                unit.id,
                                "table.qrcode",
                                !unit.table?.qrcode
                              );
                            }}
                          />
                        </div>
                      </td>
                      <td style={{ minWidth: 200 }}>
                        <div className="d-flex align-center justify-between">
                          <Toggle2
                            showText
                            checked={unit.table?.smartpos?.service}
                            onChange={() => {
                              handleMappingChange(
                                unit.id,
                                "table.smartpos.service",
                                !unit.table?.smartpos?.service
                              );
                            }}
                          />
                        </div>
                      </td>
                      <td style={{ minWidth: 300 }}>
                        <Select
                          design="mini"
                          value={unit.table?.smartpos?.provider}
                          options={[
                            { label: "---", value: "" },
                            { label: "Bin", key: "3SCheckout" },
                            { label: "Caixa Azulzinha", key: "Altec" },
                            { label: "Cielo", key: "Atena" },
                            { label: "Getnet", key: "BERP" },
                            { label: "PagBank", key: "Colibri" },
                            { label: "Rede", key: "DFranquias" },
                            { label: "Safra Pay", key: "EasyAssist" },
                            { label: "Sicredi", key: "Eclética" },
                            { label: "EPOC", key: "EPOC" },
                            { label: "Stone", key: "Menew" },
                            { label: "Vero", key: "New" },
                            { label: "Outro", key: "RS" },
                          ]}
                          onChange={(e) =>
                            handleMappingChange(
                              unit.id,
                              "table.smartpos.provider",
                              e.target.value
                            )
                          }
                        />
                      </td>
                      <td style={{ minWidth: 200 }}>
                        <div className="d-flex align-center justify-between">
                          <Toggle2
                            showText
                            checked={unit.payment_on_delivery}
                            onChange={() => {
                              handleMappingChange(
                                unit.id,
                                "payment_on_delivery",
                                !unit.payment_on_delivery
                              );
                            }}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                }}
              />
            </tbody>
          </table>
        </div>

        <div className={styles.footer}>
          <Button onClick={save} design="primary">
            Salvar alterações
          </Button>
        </div>
      </div>

      <Drawer
        visible={openSuccess}
        onClose={() => setOpenSuccess(false)}
        direction="center"
        showCloseButton={false}
      >
        <div className={styles.success}>
          <p className={styles.title}>Sucesso!</p>
          <p className={styles.subtitle}>
            Suas últimas alterações foram salvas.
          </p>
          <Button
            design="primary"
            className="w-100 mb-15"
            onClick={() => setOpenSuccess(false)}
          >
            Continuar aqui
          </Button>
          <Button className="w-100" onClick={() => navigate("/implantacao")}>
            Ir para tela inicial
          </Button>
        </div>
      </Drawer>

      <Drawer
        visible={openError}
        onClose={() => setOpenError(false)}
        direction="center"
        showCloseButton={false}
      >
        <div className={styles.success}>
          <p className={styles.title}>Erro ao salvar</p>
          <p className={styles.subtitle}>{errorMessage}</p>
          <Button className="w-100" onClick={() => setOpenError(false)}>
            Voltar
          </Button>
        </div>
      </Drawer>
    </div>
  );
}
